import React, { useState } from "react";
import { toast } from "sonner";
import { CaretDown, Check, ArrowsClockwise } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { adminApi, getErrorMessage } from "@/lib/api";
import { OutcomeChip, OUTCOME_META } from "@/components/admin/OutcomeChip";

const CHOICES = ["solved", "resolved_by_team", "escalated", "outstanding", "no_question"];

function Option({ k, label, color, current, busy, disabled, hint, onPick }) {
  return (
    <button type="button" onClick={() => onPick(k)} disabled={disabled || !!busy} data-testid={`outcome-option-${k}`}
      className="w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs text-left text-neutral-300 hover:bg-white/[0.05] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent">
      {color ? <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} /> : <ArrowsClockwise size={12} className="shrink-0 text-neutral-500" />}
      <span className="flex-1 min-w-0">
        <span className="block">{label}</span>
        {hint && <span className="block text-[0.62rem] text-neutral-600">{hint}</span>}
      </span>
      {busy === k ? <span className="w-3 h-3 rounded-full border border-neutral-500 border-t-transparent animate-spin" /> : current && <Check size={12} className="text-[#73bf44]" />}
    </button>
  );
}

// Clickable outcome chip that lets an admin set (or reset) a conversation's resolution status.
export function OutcomePicker({ conv, onChange, align = "start" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);
  const manual = conv.admin_outcome;

  const pick = async (outcome) => {
    setBusy(outcome);
    try {
      const { data } = await adminApi.put(`/admin/conversations/${conv.id}/outcome`, { outcome, reason });
      toast.success(outcome === "auto" ? "Back to automatic status" : `Marked as ${OUTCOME_META[outcome].label}`);
      setOpen(false);
      setReason("");
      onChange?.(data);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(null); }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} className="inline-flex flex-col items-start gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" title="Change resolution status" data-testid="outcome-picker-trigger"
            className="group inline-flex items-center gap-1 rounded-full outline-none focus-visible:ring-1 focus-visible:ring-[#73bf44]">
            <OutcomeChip outcome={conv.outcome} reason={conv.outcome_reason} />
            <CaretDown size={10} className="text-neutral-600 group-hover:text-white transition-colors" />
          </button>
        </PopoverTrigger>
        <PopoverContent align={align} className="w-64 p-2 bg-[#0F0F0F] border-neutral-800 text-neutral-200" data-testid="outcome-picker">
          <div className="px-2 pt-1 pb-2 text-[0.6rem] uppercase tracking-[0.18em] font-bold text-neutral-500">Set resolution status</div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="Reason (optional)" data-testid="outcome-picker-reason"
            className="w-full mb-1.5 bg-black/50 border border-neutral-800 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#73bf44] placeholder:text-neutral-600 transition-colors" />
          {CHOICES.map((k) => (
            <Option key={k} k={k} label={OUTCOME_META[k].label} color={OUTCOME_META[k].color} current={manual === k} busy={busy} onPick={pick} />
          ))}
          <div className="border-t border-neutral-800 my-1.5" />
          <Option k="auto" label="Automatic" hint="AI review, thumbs and tickets decide" current={!manual} disabled={!manual} busy={busy} onPick={pick} />
        </PopoverContent>
      </Popover>
      {manual && <span className="text-[0.55rem] uppercase tracking-wider font-semibold text-neutral-500" data-testid="outcome-manual-tag">Set by admin</span>}
    </div>
  );
}
